import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { publicFetch, apiEndpoints } from '../config/api';
import yb1 from '../assets/yb (1).jpg';

const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch gallery images from API
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicFetch(apiEndpoints.images.gallery);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch gallery images: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && data.images) {
          // Map API response to component structure
          const mappedImages = data.images.map((img) => ({
            id: img._id,
            src: apiEndpoints.images.url(img.url),
            title: img.title || 'Untitled',
            category: img.category || 'gallery',
            featured: img.featured || false,
          }));
          setGalleryImages(mappedImages);
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (err) {
        console.error('Error fetching gallery images:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  // Extract unique categories from API data
  const categories = useMemo(() => {
    const uniqueCategories = ['All'];
    const categorySet = new Set();
    
    galleryImages.forEach((img) => {
      if (img.category && !categorySet.has(img.category)) {
        categorySet.add(img.category);
        uniqueCategories.push(img.category);
      }
    });
    
    return uniqueCategories;
  }, [galleryImages]);

  const filteredImages = useMemo(() => {
    if (selectedCategory === 'All') {
      return galleryImages;
    }
    return galleryImages.filter(img => img.category === selectedCategory);
  }, [galleryImages, selectedCategory]);

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredImages.length;
    } else {
      newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    }
    setSelectedImage(filteredImages[newIndex]);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
        const newIndex = (currentIndex + 1) % filteredImages.length;
        setSelectedImage(filteredImages[newIndex]);
      } else if (e.key === 'ArrowLeft') {
        const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
        const newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
        setSelectedImage(filteredImages[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedImage, filteredImages, closeLightbox]);

  return (
    <main className="ui-mdc-gallery bg-gradient-to-b from-[#1a1f28] via-[#202834] to-[#0a0d12] text-white min-h-screen">
      {/* Hero Section */}
      <section className="ui-mdc-gallery-hero relative flex min-h-[60vh] items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={galleryImages.length > 0 
              ? (galleryImages.find(img => img.featured)?.src || galleryImages[0]?.src || yb1)
              : yb1
            } 
            alt="Yala Gallery" 
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <span className="ui-mdc-gallery-kicker inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium uppercase tracking-widest text-amber-200/90">
            Visual Journey
          </span>
          <h1 className="ui-mdc-gallery-title mt-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Yala Safari Gallery
          </h1>
          <p className="ui-mdc-gallery-subtitle mt-6 text-lg text-gray-300 sm:text-xl">
            Explore the breathtaking beauty of Yala National Park through our curated collection of wildlife moments and safari adventures.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="ui-mdc-gallery-filters relative border-b border-white/10 bg-black/20 py-8">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="ui-mdc-gallery-filter-bar flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`ui-mdc-gallery-filter-btn capitalize ${
                  selectedCategory === category ? 'ui-mdc-gallery-filter-btn--active' : ''
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Image Grid */}
      <section className="ui-mdc-gallery-grid-section relative py-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="ui-mdc-gallery-loading">
                <div className="ui-mdc-gallery-loading-spinner"></div>
                <span className="mt-4 text-gray-300">Loading gallery images...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <p className="text-red-400 text-lg mb-4">Error loading gallery</p>
                <p className="text-gray-400">{error}</p>
              </div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <p className="text-gray-400 text-lg">No images found{selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}</p>
              </div>
            </div>
          ) : (
            <div className="ui-mdc-gallery-grid">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="ui-mdc-gallery-item group"
                  onClick={() => openLightbox(image)}
                >
                  <div className="ui-mdc-gallery-item-inner">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="ui-mdc-gallery-image"
                      loading="lazy"
                      onError={(e) => {
                        console.error('Failed to load image:', image.src);
                        e.target.src = yb1; // Fallback to default image
                      }}
                    />
                    <div className="ui-mdc-gallery-overlay">
                      <div className="ui-mdc-gallery-overlay-content">
                        <h3 className="ui-mdc-gallery-item-title">{image.title}</h3>
                        <span className="ui-mdc-gallery-item-category capitalize">{image.category}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="ui-mdc-gallery-lightbox fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            className="ui-mdc-gallery-lightbox-close absolute right-6 top-6 z-10"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            className="ui-mdc-gallery-lightbox-nav ui-mdc-gallery-lightbox-prev absolute left-6 z-10"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            aria-label="Previous image"
          >
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="ui-mdc-gallery-lightbox-nav ui-mdc-gallery-lightbox-next absolute right-6 z-10"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            aria-label="Next image"
          >
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="ui-mdc-gallery-lightbox-content relative max-w-6xl px-6" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="ui-mdc-gallery-lightbox-image max-h-[90vh] w-full object-contain"
            />
            <div className="ui-mdc-gallery-lightbox-info mt-4 text-center">
              <h3 className="text-2xl font-semibold text-white">{selectedImage.title}</h3>
              <span className="mt-2 inline-block rounded-full bg-[#c4915f]/20 px-4 py-1 text-sm text-amber-200">
                {selectedImage.category}
              </span>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Gallery;